import { Link } from "react-router-dom";
import { History } from "lucide-react";
import { getApi } from "../../api";
import { useAsyncResource } from "../../hooks/useAsyncResource";
import { Card } from "../Card";
import { ErrorState, Skeleton } from "../ScreenStates";
import { formatRmm } from "../../utils/format";

interface PriorYearCardProps {
  groupId: string;
  children?: never;
}

/** Renewal groups only: what happened the last time this employer was underwritten. */
export function PriorYearCard({ groupId }: PriorYearCardProps) {
  const history = useAsyncResource(() => getApi().getGroupHistory(groupId), groupId, "PriorYearCard.load");
  const { state } = history;

  return (
    <Card title="Prior Year" icon={History}>
      {state.kind === "error" && <ErrorState message={state.message} onRetry={history.reload} />}
      {state.kind === "loading" && !state.data && (
        <div className="space-y-2" role="status" aria-label="Loading group history">
          <Skeleton />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      )}
      {state.data && state.data.previouslyUnderwrittenCount === 0 && <p className="text-sm text-gray-600">No prior year group found for this employer.</p>}
      {state.data && state.data.previouslyUnderwrittenCount > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-gray-500">Prior year group</dt>
          <dd className="font-medium text-gray-800">
            {state.data.mostRecentGroupId ? (
              <Link to={`/uw/groups/${state.data.mostRecentGroupId}`} className="text-brand-navy underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-brand-green-50 rounded">
                {state.data.mostRecentGroupName}
              </Link>
            ) : (
              (state.data.mostRecentGroupName ?? "–")
            )}
          </dd>
          <dt className="text-gray-500">Prior year RMM</dt>
          <dd className="font-medium tabular-nums text-gray-800">{formatRmm(state.data.mostRecentRmm) || "–"}</dd>
          <dt className="text-gray-500">Sold last year</dt>
          <dd className="font-medium text-gray-800">{state.data.previouslySold ? "Yes" : "No"}</dd>
          <dt className="text-gray-500">Times underwritten</dt>
          <dd className="font-medium tabular-nums text-gray-800">{state.data.previouslyUnderwrittenCount}</dd>
        </dl>
      )}
    </Card>
  );
}
